import type { LocaleCode } from "@/pages/cogo-marketing/i18n/locales";

export interface OpenPencilPrimarySourceCopy {
  panels: {
    designSource: string;
    exportCurrent: string;
    exporting: string;
    canvas: string;
    sourceUseEvidence: string;
    showDetails: string;
    hideDetails: string;
    frameCount: (count: number) => string;
    pages: string;
    nodes: string;
    sections: string;
    candidates: string;
    mobile: string;
    reference: string;
    variables: string;
    colors: string;
    sourceRoundtrip: string;
    generationReadiness: string;
  };
  editor: {
    opening: string;
    loadingSource: string;
    loadingCanvas: string;
    preparing: string;
    ready: string;
    saving: string;
    saved: string;
    saveFailed: string;
    openFailed: string;
    canvasUnavailable: string;
    canvasCreateFailed: string;
    pageMissing: string;
    unsaved: string;
    undo: string;
    redo: string;
    saveFig: string;
    editableCanvas: string;
    selectedLayer: string;
    name: string;
    text: string;
    positionX: string;
    positionY: string;
    width: string;
    height: string;
    fontLoadError: string;
    selectionHelp: string;
    noSelection: string;
  };
}

export const OPENPENCIL_PRIMARY_SOURCE_COPY: Record<
  LocaleCode,
  OpenPencilPrimarySourceCopy
> = {
  en: {
    panels: {
      designSource: "Design source", exportCurrent: "Export current", exporting: "Exporting",
      canvas: "Canvas", sourceUseEvidence: "Source use evidence",
      showDetails: "Show design source details", hideDetails: "Hide design source details, and give the canvas the space",
      frameCount: (count) => `${count} frames`, pages: "Pages", nodes: "Nodes", sections: "Sections",
      candidates: "Candidates", mobile: "Mobile", reference: "Reference", variables: "Variables", colors: "Colors",
      sourceRoundtrip: "Source roundtrip", generationReadiness: "Generation readiness",
    },
    editor: {
      opening: "Opening Figma design…", loadingSource: "Loading Figma source", loadingCanvas: "Loading the design canvas",
      preparing: "Preparing the Figma editor", ready: "Figma design editor", saving: "Saving .fig file…",
      saved: "Saved as a Figma .fig file", saveFailed: "Could not save the .fig file.",
      openFailed: "The Figma design could not be opened in the browser.", canvasUnavailable: "The editor canvas is unavailable.",
      canvasCreateFailed: "The browser design canvas could not be created.", pageMissing: "This Figma file does not contain a page.",
      unsaved: "Unsaved changes", undo: "Undo", redo: "Redo", saveFig: "Save .fig",
      editableCanvas: "Editable Figma design canvas", selectedLayer: "Selected layer", name: "Name", text: "Text",
      positionX: "X", positionY: "Y", width: "Width", height: "Height", fontLoadError: "Unknown font loading error",
      selectionHelp: "Drag selected layers to move them. Save downloads an editable Figma `.fig` file.",
      noSelection: "Click a layer to edit it.",
    },
  },
  ko: {
    panels: {
      designSource: "디자인 소스", exportCurrent: "현재 소스 내보내기", exporting: "내보내는 중",
      canvas: "캔버스", sourceUseEvidence: "소스 사용 증거",
      showDetails: "디자인 소스 상세 보기", hideDetails: "디자인 소스 상세를 숨기고 캔버스 공간 확보",
      frameCount: (count) => `프레임 ${count}개`, pages: "페이지", nodes: "노드", sections: "섹션",
      candidates: "후보", mobile: "모바일", reference: "참조", variables: "변수", colors: "색상",
      sourceRoundtrip: "소스 왕복 검증", generationReadiness: "생성 준비 상태",
    },
    editor: {
      opening: "Figma 디자인 여는 중…", loadingSource: "Figma 소스 불러오는 중", loadingCanvas: "디자인 캔버스 불러오는 중",
      preparing: "Figma 편집기 준비 중", ready: "Figma 디자인 편집기", saving: ".fig 파일 저장 중…",
      saved: "Figma .fig 파일로 저장됨", saveFailed: ".fig 파일을 저장하지 못했습니다.",
      openFailed: "브라우저에서 Figma 디자인을 열지 못했습니다.", canvasUnavailable: "편집기 캔버스를 사용할 수 없습니다.",
      canvasCreateFailed: "브라우저 디자인 캔버스를 만들지 못했습니다.", pageMissing: "이 Figma 파일에는 페이지가 없습니다.",
      unsaved: "저장하지 않은 변경", undo: "실행 취소", redo: "다시 실행", saveFig: ".fig 저장",
      editableCanvas: "편집 가능한 Figma 디자인 캔버스", selectedLayer: "선택한 레이어", name: "이름", text: "텍스트",
      positionX: "가로 위치", positionY: "세로 위치", width: "너비", height: "높이", fontLoadError: "글꼴을 불러오지 못했습니다.",
      selectionHelp: "선택한 레이어를 드래그해 이동하세요. 저장하면 편집 가능한 Figma `.fig` 파일을 내려받습니다.",
      noSelection: "편집할 레이어를 클릭하세요.",
    },
  },
  ja: {
    panels: {
      designSource: "デザインソース", exportCurrent: "現在のソースを書き出す", exporting: "書き出し中",
      canvas: "キャンバス", sourceUseEvidence: "ソース利用の証拠",
      showDetails: "デザインソースの詳細を表示", hideDetails: "デザインソースの詳細を隠してキャンバスを広げる",
      frameCount: (count) => `${count}フレーム`, pages: "ページ", nodes: "ノード", sections: "セクション",
      candidates: "候補", mobile: "モバイル", reference: "参照", variables: "変数", colors: "色",
      sourceRoundtrip: "ソース往復検証", generationReadiness: "生成の準備状況",
    },
    editor: {
      opening: "Figmaデザインを開いています…", loadingSource: "Figmaソースを読み込み中", loadingCanvas: "デザインキャンバスを読み込み中",
      preparing: "Figmaエディターを準備中", ready: "Figmaデザインエディター", saving: ".figファイルを保存中…",
      saved: "Figma .figファイルとして保存しました", saveFailed: ".figファイルを保存できませんでした。",
      openFailed: "ブラウザーでFigmaデザインを開けませんでした。", canvasUnavailable: "エディターキャンバスを利用できません。",
      canvasCreateFailed: "ブラウザーデザインキャンバスを作成できませんでした。", pageMissing: "このFigmaファイルにページがありません。",
      unsaved: "未保存の変更", undo: "元に戻す", redo: "やり直す", saveFig: ".figを保存",
      editableCanvas: "編集可能なFigmaデザインキャンバス", selectedLayer: "選択中のレイヤー", name: "名前", text: "テキスト",
      positionX: "X", positionY: "Y", width: "幅", height: "高さ", fontLoadError: "フォントを読み込めませんでした。",
      selectionHelp: "選択したレイヤーをドラッグして移動できます。保存すると編集可能なFigma `.fig`ファイルをダウンロードします。",
      noSelection: "編集するレイヤーをクリックしてください。",
    },
  },
  zh: {
    panels: {
      designSource: "设计来源", exportCurrent: "导出当前来源", exporting: "正在导出",
      canvas: "画布", sourceUseEvidence: "来源使用证据",
      showDetails: "显示设计来源详情", hideDetails: "隐藏设计来源详情并腾出画布空间",
      frameCount: (count) => `${count} 个画框`, pages: "页面", nodes: "节点", sections: "区段",
      candidates: "候选", mobile: "移动端", reference: "参考", variables: "变量", colors: "颜色",
      sourceRoundtrip: "来源往返验证", generationReadiness: "生成准备状态",
    },
    editor: {
      opening: "正在打开 Figma 设计…", loadingSource: "正在加载 Figma 来源", loadingCanvas: "正在加载设计画布",
      preparing: "正在准备 Figma 编辑器", ready: "Figma 设计编辑器", saving: "正在保存 .fig 文件…",
      saved: "已保存为 Figma .fig 文件", saveFailed: "无法保存 .fig 文件。",
      openFailed: "无法在浏览器中打开 Figma 设计。", canvasUnavailable: "编辑器画布不可用。",
      canvasCreateFailed: "无法创建设计画布。", pageMissing: "此 Figma 文件不包含页面。",
      unsaved: "未保存的更改", undo: "撤销", redo: "重做", saveFig: "保存 .fig",
      editableCanvas: "可编辑的 Figma 设计画布", selectedLayer: "已选图层", name: "名称", text: "文本",
      positionX: "横向位置", positionY: "纵向位置", width: "宽度", height: "高度", fontLoadError: "无法加载字体。",
      selectionHelp: "拖动已选图层即可移动。保存会下载可编辑的 Figma `.fig` 文件。",
      noSelection: "点击图层进行编辑。",
    },
  },
  "zh-TW": {
    panels: {
      designSource: "設計來源", exportCurrent: "匯出目前來源", exporting: "正在匯出",
      canvas: "畫布", sourceUseEvidence: "來源使用證據",
      showDetails: "顯示設計來源詳情", hideDetails: "隱藏設計來源詳情並騰出畫布空間",
      frameCount: (count) => `${count} 個畫框`, pages: "頁面", nodes: "節點", sections: "區段",
      candidates: "候選", mobile: "行動版", reference: "參考", variables: "變數", colors: "顏色",
      sourceRoundtrip: "來源往返驗證", generationReadiness: "生成準備狀態",
    },
    editor: {
      opening: "正在開啟 Figma 設計…", loadingSource: "正在載入 Figma 來源", loadingCanvas: "正在載入設計畫布",
      preparing: "正在準備 Figma 編輯器", ready: "Figma 設計編輯器", saving: "正在儲存 .fig 檔案…",
      saved: "已儲存為 Figma .fig 檔案", saveFailed: "無法儲存 .fig 檔案。",
      openFailed: "無法在瀏覽器中開啟 Figma 設計。", canvasUnavailable: "編輯器畫布無法使用。",
      canvasCreateFailed: "無法建立設計畫布。", pageMissing: "此 Figma 檔案不包含頁面。",
      unsaved: "未儲存的變更", undo: "還原", redo: "重做", saveFig: "儲存 .fig",
      editableCanvas: "可編輯的 Figma 設計畫布", selectedLayer: "已選圖層", name: "名稱", text: "文字",
      positionX: "水平位置", positionY: "垂直位置", width: "寬度", height: "高度", fontLoadError: "無法載入字型。",
      selectionHelp: "拖曳已選圖層即可移動。儲存會下載可編輯的 Figma `.fig` 檔案。",
      noSelection: "點選圖層進行編輯。",
    },
  },
};

const EN_SOURCE_PANEL_COPY = {
  noDesignImported: "No `.fig` design has been imported yet.",
  generationAdmissionBlocked: "Generation admission: blocked",
  preferenceNotSaved: "This browser could not remember the panel setting. The collapsed default will be used next time.",
  metrics: {
    pages: "Pages", nodes: "Nodes", sections: "Sections", candidates: "Candidates",
    mobile: "Mobile", reference: "Reference", variables: "Variables", colors: "Colors",
  },
  sourceRevision: (source: number, workspace: number, pending: number) =>
    `Source revision ${source} · Workspace revision ${workspace} · ${pending} surfaces pending`,
  fromDesignFile: (revision: number) => `From your design file · revision ${revision}`,
  roundtrip: "Source roundtrip",
  rights: {
    show: "Show source-use evidence", hide: "Hide source-use evidence",
    question: "How may this source be used?", owned: "I own this design",
    ownedDetail: "Record it for this project and Pack review.", licensed: "I have a license or permission",
    licensedDetail: "A contract, approval, or license reference is required.", evaluation: "Evaluation only",
    evaluationDetail: "Production use and Pack promotion remain disabled.", licenseReference: "License or permission reference",
    licensePlaceholder: "Agreement ID, approval reference, or URL",
    help: "Your statement is saved as evidence for this exact revision. It does not verify legal rights, issue a signed rights record, or publish a Design System Pack. An outside review is still required.",
    evaluationStatus: "Evaluation-only · Pack promotion disabled",
    recordedStatus: "Evidence recorded · rights review pending · Pack promotion blocked",
    recordFailed: "Could not record source use evidence.", recording: "Recording", record: "Record evidence",
  },
  readiness: {
    title: "Generation readiness", show: "Show readiness gates", hide: "Hide readiness gates",
    readyCount: (ready: number, total: number) => `${ready}/${total} gates ready`,
    next: (label: string) => `Next: ${label}`, complete: "All required gates are complete",
    sourceMapping: "Source mapping", designTokens: "Design tokens", visualEvidence: "Visual evidence",
    revisionDiff: "Revision diff", assembly: "Assembly", candidate: "Candidate", runtimeProof: "Runtime proof",
    surfacesSelected: (accepted: number, total: number) => `${accepted}/${total} surfaces selected`,
    decisionsPending: (pending: number) => `${pending} decisions pending`,
    previews: (rendered: number, requested: number, judgements: number) => `${rendered}/${requested} previews · ${judgements} judgements`,
    changed: (count: number, disposition: string) => `${count} changed · ${disposition}`,
    notRequired: "not required",
  },
  advanced: {
    title: "Advanced source review", show: "Show advanced source review", hide: "Hide advanced source review",
    summary: (pages: number, surfaces: number, tokens: number) => `${pages} pages · ${surfaces} surfaces · ${tokens} token decisions pending`,
  },
  revision: {
    title: "Revision visual diff", changedSurfaces: "Changed surfaces", disposition: "Disposition", status: "Status",
    reviewRequired: "Visual review required",
    reviewDetail: (count: number) => `The edited revision changed source pixels on ${count} surfaces. A human or Gemma judgement must approve those intentional deltas before generation.`,
  },
  sourceTree: "Source tree", applySuggestions: "Apply suggestions",
  pageNodes: (nodes: number, sections: number) => `${nodes} nodes · ${sections} story sections`,
  mapper: "Page/story mapper", page: "Page",
  browserProof: "Web Worker · no extension · bounded .fig export/reopen verified (aggregate-only)",
  revisionAdmission: { admitted: "canonical revision admitted", rejected: "browser-only candidate; canonical admission not completed", pending: "canonical admission pending" },
  component: {
    title: "Component variant editor", detail: "Runs the bounded edit and .fig reopen in a browser worker; the server admits the resulting revision only after proof.",
    revising: "Revising", create: "Create component revision", set: "Component set", variant: "Variant",
    sourceOnly: "Read from the design only", extractor: "extractor", sampled: "sampled", valueCoverage: "value coverage",
    complete: "complete", partial: "partial", legacyUnknown: "legacy/unknown", editableSnapshot: "editable snapshot",
    available: "available", unavailable: "unavailable",
    semanticBoundary: "No Cogo semantic role or behavior is inferred from these source names. INSTANCE_SWAP entries remain unresolved slot candidates until a curated contract admits them.",
    anatomy: "anatomy", descendantEvidence: "descendant evidence", bounded: "bounded", truncated: "truncated",
    unresolvedSlots: "unresolved slots", setIdentity: "set identity", variantIdentity: "variant identity",
    overrideOpacity: "Override component opacity", opacity: "Component opacity", overrideFill: "Override component fill",
    fillColor: "Component fill color", fillBoundary: "Only the first SOLID fill can be edited. Gradients and image fills are rejected.",
  },
  frame: {
    title: "Source frame editor", retained: "Creates a new .fig revision. The retained original is not overwritten.",
    boundary: "Only name, width, height, visibility, and opacity can be revised here. Colour edits use the component editor and remain limited to the first SOLID fill.",
    revising: "Revising", create: "Create revision", name: "Frame name", width: "Width", height: "Height",
    visibility: "Visibility", keep: "Keep source value", visible: "Visible", hidden: "Hidden",
    overrideOpacity: "Override opacity", opacity: "Frame opacity", noCandidates: "No surface candidates on this page.", candidate: "candidate", role: "role",
  },
  decisions: { accept: "Accept", defer: "Defer", reject: "Reject", storyTitle: "Story title" },
  statusLabel: (value: string) => value,
  surfaceClassification: (value: string) => value,
  surfaceRole: (value: string) => value,
  tokens: {
    title: "Design token candidates", acceptAll: "Accept all token candidates", rejectAll: "Reject all token candidates",
    summary: (pending: number, accepted: number, samples: number) => `${pending} pending · ${accepted} accepted · ${samples} visual samples`,
    applying: "Applying…", apply: "Apply these to this project", reapply: "Re-apply to this project",
  },
  proof: { title: "What this proves", accepted: "Accepted", notClaimed: "Not claimed", generation: "Generation" },
};

const KO_SOURCE_PANEL_COPY: typeof EN_SOURCE_PANEL_COPY = {
  noDesignImported: "아직 가져온 `.fig` 디자인이 없습니다.",
  generationAdmissionBlocked: "생성 승인: 차단됨",
  preferenceNotSaved: "이 브라우저가 패널 설정을 기억하지 못했습니다. 다음에는 기본 접힘 상태로 표시됩니다.",
  metrics: { pages: "페이지", nodes: "노드", sections: "섹션", candidates: "후보", mobile: "모바일", reference: "참조", variables: "변수", colors: "색상" },
  sourceRevision: (source, workspace, pending) => `소스 리비전 ${source} · 작업공간 리비전 ${workspace} · 검토 대기 화면 ${pending}개`,
  fromDesignFile: (revision) => `디자인 파일에서 읽음 · 리비전 ${revision}`,
  roundtrip: "소스 왕복 검증",
  rights: {
    show: "소스 사용 근거 보기", hide: "소스 사용 근거 숨기기", question: "이 소스를 어떤 범위로 사용할 수 있나요?",
    owned: "내가 소유한 디자인입니다", ownedDetail: "이 프로젝트와 Pack 검토 근거로 기록합니다.",
    licensed: "라이선스 또는 사용 허가가 있습니다", licensedDetail: "계약, 승인 또는 라이선스 참조가 필요합니다.",
    evaluation: "평가 목적으로만 사용", evaluationDetail: "상용 사용과 Pack 승격은 계속 차단됩니다.",
    licenseReference: "라이선스 또는 사용 허가 참조", licensePlaceholder: "계약 ID, 승인 번호 또는 URL",
    help: "이 진술은 현재 리비전의 근거로 저장됩니다. 법적 권리를 검증하거나 서명된 권리 기록을 발급하거나 Design System Pack을 발행하지는 않습니다. 별도 검토가 필요합니다.",
    evaluationStatus: "평가 전용 · Pack 승격 차단", recordedStatus: "사용 근거 기록됨 · 권리 검토 대기 · Pack 승격 차단",
    recordFailed: "소스 사용 근거를 기록하지 못했습니다.", recording: "기록 중", record: "근거 기록",
  },
  readiness: {
    title: "생성 준비 상태", show: "준비 조건 상세 보기", hide: "준비 조건 상세 숨기기",
    readyCount: (ready, total) => `${total}개 조건 중 ${ready}개 준비됨`, next: (label) => `다음 작업: ${label}`,
    complete: "필수 준비 조건이 모두 완료되었습니다", sourceMapping: "소스 매핑", designTokens: "디자인 토큰",
    visualEvidence: "시각 증거", revisionDiff: "리비전 차이", assembly: "조립 검증", candidate: "후보 연결", runtimeProof: "런타임 증명",
    surfacesSelected: (accepted, total) => `화면 ${total}개 중 ${accepted}개 선택`, decisionsPending: (pending) => `결정 대기 ${pending}개`,
    previews: (rendered, requested, judgements) => `미리보기 ${requested}개 중 ${rendered}개 · 판정 ${judgements}개`,
    changed: (count, disposition) => `변경 ${count}개 · ${disposition}`, notRequired: "필요 없음",
  },
  advanced: {
    title: "고급 소스 검토", show: "고급 소스 검토 열기", hide: "고급 소스 검토 닫기",
    summary: (pages, surfaces, tokens) => `페이지 ${pages}개 · 화면 ${surfaces}개 · 토큰 결정 대기 ${tokens}개`,
  },
  revision: {
    title: "리비전 시각 차이", changedSurfaces: "변경된 화면", disposition: "판정", status: "상태", reviewRequired: "시각 검토 필요",
    reviewDetail: (count) => `편집된 리비전이 화면 ${count}개의 픽셀을 변경했습니다. 생성 전에 사람 또는 Gemma가 의도된 변경을 승인해야 합니다.`,
  },
  sourceTree: "소스 트리", applySuggestions: "제안 적용", pageNodes: (nodes, sections) => `노드 ${nodes}개 · 스토리 섹션 ${sections}개`,
  mapper: "페이지/스토리 매핑", page: "페이지", browserProof: "Web Worker · 확장 프로그램 없음 · 제한된 .fig 내보내기/재열기 검증(집계만)",
  revisionAdmission: { admitted: "정본 리비전 승인됨", rejected: "브라우저 전용 후보 · 정본 승인 미완료", pending: "정본 승인 대기" },
  component: {
    title: "컴포넌트 변형 편집기", detail: "브라우저 Worker에서 제한된 편집과 .fig 재열기를 수행하며, 서버는 증명 후 결과 리비전만 승인합니다.",
    revising: "리비전 생성 중", create: "컴포넌트 리비전 생성", set: "컴포넌트 세트", variant: "변형",
    sourceOnly: "디자인에서 읽은 값만 사용", extractor: "추출기", sampled: "표본", valueCoverage: "값 범위",
    complete: "완전", partial: "부분", legacyUnknown: "레거시/알 수 없음", editableSnapshot: "편집 가능 스냅샷",
    available: "있음", unavailable: "없음",
    semanticBoundary: "소스 이름만으로 Cogo 의미 역할이나 동작을 추론하지 않습니다. INSTANCE_SWAP 항목은 검증된 계약이 승인할 때까지 미해결 슬롯 후보로 남습니다.",
    anatomy: "구조", descendantEvidence: "하위 항목 근거", bounded: "제한", truncated: "잘림", unresolvedSlots: "미해결 슬롯",
    setIdentity: "세트 식별자", variantIdentity: "변형 식별자", overrideOpacity: "컴포넌트 불투명도 변경",
    opacity: "컴포넌트 불투명도", overrideFill: "컴포넌트 채우기 변경", fillColor: "컴포넌트 채우기 색상",
    fillBoundary: "첫 번째 단색 채우기만 편집할 수 있습니다. 그라데이션과 이미지 채우기는 거부됩니다.",
  },
  frame: {
    title: "소스 프레임 편집기", retained: "새 .fig 리비전을 만들며 보관된 원본은 덮어쓰지 않습니다.",
    boundary: "여기서는 이름, 너비, 높이, 표시 여부, 불투명도만 변경할 수 있습니다. 색상은 컴포넌트 편집기에서 첫 번째 단색 채우기만 변경합니다.",
    revising: "리비전 생성 중", create: "리비전 생성", name: "프레임 이름", width: "너비", height: "높이", visibility: "표시 여부",
    keep: "소스 값 유지", visible: "표시", hidden: "숨김", overrideOpacity: "불투명도 변경", opacity: "프레임 불투명도",
    noCandidates: "이 페이지에는 화면 후보가 없습니다.", candidate: "후보", role: "역할",
  },
  decisions: { accept: "승인", defer: "보류", reject: "거부", storyTitle: "스토리 제목" },
  statusLabel: (value) =>
    (({
      pending: "대기", accepted: "승인됨", rejected: "거부됨", approved: "승인됨",
      blocked: "차단됨", admitted: "승인됨", available: "사용 가능", unavailable: "사용 불가",
      complete: "완료", completed: "완료", missing: "미완료", bound: "연결됨",
      passed: "통과", required: "필요", optional: "선택",
    } as Record<string, string>)[value] ?? value),
  surfaceClassification: (value) =>
    (({
      mobile: "모바일",
      reference: "참조",
      "non-mobile": "참조 화면",
    } as Record<string, string>)[value] ?? value),
  surfaceRole: (value) =>
    (({
      screen: "화면", reference: "참조", annotation: "주석", cover: "표지", history: "이력",
    } as Record<string, string>)[value] ?? value),
  tokens: {
    title: "디자인 토큰 후보", acceptAll: "모든 토큰 후보 승인", rejectAll: "모든 토큰 후보 거부",
    summary: (pending, accepted, samples) => `대기 ${pending}개 · 승인 ${accepted}개 · 시각 표본 ${samples}개`,
    applying: "적용 중…", apply: "이 프로젝트에 적용", reapply: "이 프로젝트에 다시 적용",
  },
  proof: { title: "증명 범위", accepted: "확인됨", notClaimed: "주장하지 않음", generation: "생성 조건" },
};

const JA_SOURCE_PANEL_COPY: typeof EN_SOURCE_PANEL_COPY = {
  noDesignImported: "まだ`.fig`デザインが読み込まれていません。",
  generationAdmissionBlocked: "生成承認：ブロック中",
  preferenceNotSaved: "このブラウザーではパネル設定を保存できませんでした。次回は既定の折りたたみ状態で表示されます。",
  metrics: { pages: "ページ", nodes: "ノード", sections: "セクション", candidates: "候補", mobile: "モバイル", reference: "参照", variables: "変数", colors: "色" },
  sourceRevision: (source, workspace, pending) => `ソースリビジョン ${source} · ワークスペースリビジョン ${workspace} · 確認待ち画面 ${pending}件`,
  fromDesignFile: (revision) => `デザインファイルから取得 · リビジョン ${revision}`,
  roundtrip: "ソース往復検証",
  rights: {
    show: "ソース利用の証拠を表示", hide: "ソース利用の証拠を非表示", question: "このソースをどの範囲で使用できますか？",
    owned: "このデザインを所有しています", ownedDetail: "このプロジェクトとPack審査の証拠として記録します。",
    licensed: "ライセンスまたは利用許可があります", licensedDetail: "契約、承認、またはライセンスの参照情報が必要です。",
    evaluation: "評価目的のみ", evaluationDetail: "本番利用とPackへの昇格は引き続き無効です。",
    licenseReference: "ライセンスまたは利用許可の参照", licensePlaceholder: "契約ID、承認番号、またはURL",
    help: "この申告は、このリビジョンの証拠として保存されます。法的権利の検証、署名済み権利記録の発行、Design System Packの公開は行いません。別途審査が必要です。",
    evaluationStatus: "評価専用 · Packへの昇格は無効", recordedStatus: "利用証拠を記録済み · 権利審査待ち · Packへの昇格はブロック中",
    recordFailed: "ソース利用の証拠を記録できませんでした。", recording: "記録中", record: "証拠を記録",
  },
  readiness: {
    title: "生成の準備状況", show: "準備条件の詳細を表示", hide: "準備条件の詳細を非表示",
    readyCount: (ready, total) => `${total}件中${ready}件の条件が準備済み`, next: (label) => `次の作業：${label}`,
    complete: "必須の準備条件がすべて完了しました", sourceMapping: "ソースマッピング", designTokens: "デザイントークン",
    visualEvidence: "視覚的証拠", revisionDiff: "リビジョン差分", assembly: "組み立て検証", candidate: "候補接続", runtimeProof: "ランタイム証明",
    surfacesSelected: (accepted, total) => `${total}画面中${accepted}画面を選択`, decisionsPending: (pending) => `判断待ち ${pending}件`,
    previews: (rendered, requested, judgements) => `プレビュー ${requested}件中${rendered}件 · 判定 ${judgements}件`,
    changed: (count, disposition) => `変更 ${count}件 · ${disposition}`, notRequired: "不要",
  },
  advanced: {
    title: "高度なソースレビュー", show: "高度なソースレビューを開く", hide: "高度なソースレビューを閉じる",
    summary: (pages, surfaces, tokens) => `ページ ${pages}件 · 画面 ${surfaces}件 · トークン判断待ち ${tokens}件`,
  },
  revision: {
    title: "リビジョンのビジュアル差分", changedSurfaces: "変更された画面", disposition: "判定", status: "状態", reviewRequired: "視覚確認が必要",
    reviewDetail: (count) => `編集したリビジョンにより${count}画面のソースピクセルが変更されました。生成前に、担当者またはGemmaが意図した差分を承認する必要があります。`,
  },
  sourceTree: "ソースツリー", applySuggestions: "提案を適用", pageNodes: (nodes, sections) => `ノード ${nodes}件 · ストーリーセクション ${sections}件`,
  mapper: "ページ／ストーリーマッパー", page: "ページ", browserProof: "Web Worker · 拡張機能なし · 制限付き.fig書き出し／再読み込みを検証済み（集計のみ）",
  revisionAdmission: { admitted: "正規リビジョンを承認済み", rejected: "ブラウザー専用候補 · 正規承認は未完了", pending: "正規承認待ち" },
  component: {
    title: "コンポーネントバリアントエディター", detail: "ブラウザーWorkerで制限付き編集と.fig再読み込みを実行し、サーバーは証明後の結果リビジョンのみを承認します。",
    revising: "リビジョンを作成中", create: "コンポーネントリビジョンを作成", set: "コンポーネントセット", variant: "バリアント",
    sourceOnly: "デザインから読み取った値のみ使用", extractor: "抽出器", sampled: "サンプル済み", valueCoverage: "値の網羅率",
    complete: "完全", partial: "一部", legacyUnknown: "レガシー／不明", editableSnapshot: "編集可能なスナップショット",
    available: "利用可能", unavailable: "利用不可",
    semanticBoundary: "ソース名からCogoの意味的な役割や動作を推測しません。INSTANCE_SWAP項目は、精査済み契約で承認されるまで未解決のスロット候補として残ります。",
    anatomy: "構造", descendantEvidence: "子孫要素の証拠", bounded: "制限あり", truncated: "省略あり", unresolvedSlots: "未解決スロット",
    setIdentity: "セット識別子", variantIdentity: "バリアント識別子", overrideOpacity: "コンポーネントの不透明度を変更",
    opacity: "コンポーネントの不透明度", overrideFill: "コンポーネントの塗りを変更", fillColor: "コンポーネントの塗り色",
    fillBoundary: "最初の単色塗りのみ編集できます。グラデーションと画像塗りは拒否されます。",
  },
  frame: {
    title: "ソースフレームエディター", retained: "新しい.figリビジョンを作成します。保存済みの原本は上書きされません。",
    boundary: "ここでは名前、幅、高さ、表示状態、不透明度のみ変更できます。色の変更はコンポーネントエディターを使用し、最初の単色塗りに限定されます。",
    revising: "リビジョンを作成中", create: "リビジョンを作成", name: "フレーム名", width: "幅", height: "高さ", visibility: "表示状態",
    keep: "ソース値を維持", visible: "表示", hidden: "非表示", overrideOpacity: "不透明度を変更", opacity: "フレームの不透明度",
    noCandidates: "このページに画面候補はありません。", candidate: "候補", role: "役割",
  },
  decisions: { accept: "承認", defer: "保留", reject: "却下", storyTitle: "ストーリータイトル" },
  statusLabel: (value) =>
    (({
      pending: "待機中", accepted: "承認済み", rejected: "却下済み", approved: "承認済み",
      blocked: "ブロック中", admitted: "承認済み", available: "利用可能", unavailable: "利用不可",
      complete: "完了", completed: "完了", missing: "未完了", bound: "接続済み",
      passed: "合格", required: "必須", optional: "任意",
    } as Record<string, string>)[value] ?? value),
  surfaceClassification: (value) =>
    (({ mobile: "モバイル", reference: "参照", "non-mobile": "参照画面" } as Record<string, string>)[value] ?? value),
  surfaceRole: (value) =>
    (({ screen: "画面", reference: "参照", annotation: "注釈", cover: "表紙", history: "履歴" } as Record<string, string>)[value] ?? value),
  tokens: {
    title: "デザイントークン候補", acceptAll: "すべてのトークン候補を承認", rejectAll: "すべてのトークン候補を却下",
    summary: (pending, accepted, samples) => `待機 ${pending}件 · 承認済み ${accepted}件 · 視覚サンプル ${samples}件`,
    applying: "適用中…", apply: "このプロジェクトに適用", reapply: "このプロジェクトに再適用",
  },
  proof: { title: "証明範囲", accepted: "確認済み", notClaimed: "対象外", generation: "生成条件" },
};

const ZH_SOURCE_PANEL_COPY: typeof EN_SOURCE_PANEL_COPY = {
  noDesignImported: "尚未导入`.fig`设计。",
  generationAdmissionBlocked: "生成准入：已阻止",
  preferenceNotSaved: "此浏览器无法保存面板设置。下次将使用默认折叠状态。",
  metrics: { pages: "页面", nodes: "节点", sections: "区段", candidates: "候选", mobile: "移动端", reference: "参考", variables: "变量", colors: "颜色" },
  sourceRevision: (source, workspace, pending) => `来源版本 ${source} · 工作区版本 ${workspace} · ${pending} 个界面待审查`,
  fromDesignFile: (revision) => `读取自设计文件 · 版本 ${revision}`,
  roundtrip: "来源往返验证",
  rights: {
    show: "显示来源使用凭据", hide: "隐藏来源使用凭据", question: "此来源可用于哪些范围？",
    owned: "我拥有此设计", ownedDetail: "将其记录为此项目和Pack审查的凭据。",
    licensed: "我拥有许可证或使用许可", licensedDetail: "需要提供合同、批准或许可证参考。",
    evaluation: "仅用于评估", evaluationDetail: "生产使用和Pack晋级仍处于禁用状态。",
    licenseReference: "许可证或使用许可参考", licensePlaceholder: "合同ID、批准编号或URL",
    help: "此声明会保存为该版本的凭据。它不会验证法律权利、签发已签署的权利记录或发布Design System Pack，仍需另行审查。",
    evaluationStatus: "仅限评估 · Pack晋级已禁用", recordedStatus: "使用凭据已记录 · 权利审查待处理 · Pack晋级已阻止",
    recordFailed: "无法记录来源使用凭据。", recording: "正在记录", record: "记录凭据",
  },
  readiness: {
    title: "生成准备状态", show: "显示准备条件详情", hide: "隐藏准备条件详情",
    readyCount: (ready, total) => `${total} 个条件中 ${ready} 个已就绪`, next: (label) => `下一步：${label}`,
    complete: "所有必需的准备条件均已完成", sourceMapping: "来源映射", designTokens: "设计令牌",
    visualEvidence: "视觉凭据", revisionDiff: "版本差异", assembly: "组装验证", candidate: "候选连接", runtimeProof: "运行时证明",
    surfacesSelected: (accepted, total) => `${total} 个界面中已选择 ${accepted} 个`, decisionsPending: (pending) => `${pending} 项决策待处理`,
    previews: (rendered, requested, judgements) => `${requested} 个预览中已生成 ${rendered} 个 · ${judgements} 项判定`,
    changed: (count, disposition) => `${count} 项变更 · ${disposition}`, notRequired: "无需执行",
  },
  advanced: {
    title: "高级来源审查", show: "打开高级来源审查", hide: "关闭高级来源审查",
    summary: (pages, surfaces, tokens) => `${pages} 个页面 · ${surfaces} 个界面 · ${tokens} 项令牌决策待处理`,
  },
  revision: {
    title: "版本视觉差异", changedSurfaces: "已变更界面", disposition: "处理结果", status: "状态", reviewRequired: "需要视觉审查",
    reviewDetail: (count) => `编辑后的版本改变了 ${count} 个界面的来源像素。生成前，必须由人工或Gemma批准这些预期差异。`,
  },
  sourceTree: "来源树", applySuggestions: "应用建议", pageNodes: (nodes, sections) => `${nodes} 个节点 · ${sections} 个故事区段`,
  mapper: "页面／故事映射", page: "页面", browserProof: "Web Worker · 无扩展程序 · 已验证受限的.fig导出／重新打开（仅汇总）",
  revisionAdmission: { admitted: "规范版本已准入", rejected: "仅浏览器候选 · 规范准入未完成", pending: "规范准入待处理" },
  component: {
    title: "组件变体编辑器", detail: "在浏览器Worker中执行受限编辑并重新打开.fig；服务器仅在验证后准入结果版本。",
    revising: "正在创建版本", create: "创建组件版本", set: "组件集", variant: "变体",
    sourceOnly: "仅使用设计中读取的值", extractor: "提取器", sampled: "已采样", valueCoverage: "值覆盖率",
    complete: "完整", partial: "部分", legacyUnknown: "旧版／未知", editableSnapshot: "可编辑快照",
    available: "可用", unavailable: "不可用",
    semanticBoundary: "不会根据来源名称推断Cogo语义角色或行为。在精选契约准入前，INSTANCE_SWAP条目仍是未解决的插槽候选。",
    anatomy: "结构", descendantEvidence: "后代证据", bounded: "受限", truncated: "已截断", unresolvedSlots: "未解决插槽",
    setIdentity: "组件集标识", variantIdentity: "变体标识", overrideOpacity: "覆盖组件不透明度",
    opacity: "组件不透明度", overrideFill: "覆盖组件填充", fillColor: "组件填充颜色",
    fillBoundary: "仅可编辑第一个纯色填充。渐变和图像填充会被拒绝。",
  },
  frame: {
    title: "来源画框编辑器", retained: "创建新的.fig版本，不会覆盖保留的原始文件。",
    boundary: "此处仅可修改名称、宽度、高度、可见性和不透明度。颜色编辑使用组件编辑器，并且仅限第一个纯色填充。",
    revising: "正在创建版本", create: "创建版本", name: "画框名称", width: "宽度", height: "高度", visibility: "可见性",
    keep: "保留来源值", visible: "可见", hidden: "隐藏", overrideOpacity: "覆盖不透明度", opacity: "画框不透明度",
    noCandidates: "此页面没有界面候选。", candidate: "候选", role: "角色",
  },
  decisions: { accept: "接受", defer: "暂缓", reject: "拒绝", storyTitle: "故事标题" },
  statusLabel: (value) =>
    (({
      pending: "待处理", accepted: "已接受", rejected: "已拒绝", approved: "已批准",
      blocked: "已阻止", admitted: "已准入", available: "可用", unavailable: "不可用",
      complete: "完成", completed: "已完成", missing: "未完成", bound: "已连接",
      passed: "通过", required: "必需", optional: "可选",
    } as Record<string, string>)[value] ?? value),
  surfaceClassification: (value) =>
    (({ mobile: "移动端", reference: "参考", "non-mobile": "参考界面" } as Record<string, string>)[value] ?? value),
  surfaceRole: (value) =>
    (({ screen: "界面", reference: "参考", annotation: "批注", cover: "封面", history: "历史" } as Record<string, string>)[value] ?? value),
  tokens: {
    title: "设计令牌候选", acceptAll: "接受所有令牌候选", rejectAll: "拒绝所有令牌候选",
    summary: (pending, accepted, samples) => `${pending} 项待处理 · ${accepted} 项已接受 · ${samples} 个视觉样本`,
    applying: "正在应用…", apply: "应用到此项目", reapply: "重新应用到此项目",
  },
  proof: { title: "证明范围", accepted: "已确认", notClaimed: "未声明", generation: "生成条件" },
};

const ZH_TW_SOURCE_PANEL_COPY: typeof EN_SOURCE_PANEL_COPY = {
  noDesignImported: "尚未匯入`.fig`設計。",
  generationAdmissionBlocked: "生成准入：已阻擋",
  preferenceNotSaved: "此瀏覽器無法儲存面板設定。下次將使用預設摺疊狀態。",
  metrics: { pages: "頁面", nodes: "節點", sections: "區段", candidates: "候選", mobile: "行動版", reference: "參考", variables: "變數", colors: "顏色" },
  sourceRevision: (source, workspace, pending) => `來源版本 ${source} · 工作區版本 ${workspace} · ${pending} 個畫面待審查`,
  fromDesignFile: (revision) => `讀取自設計檔案 · 版本 ${revision}`,
  roundtrip: "來源往返驗證",
  rights: {
    show: "顯示來源使用憑據", hide: "隱藏來源使用憑據", question: "此來源可用於哪些範圍？",
    owned: "我擁有此設計", ownedDetail: "將其記錄為此專案和Pack審查的憑據。",
    licensed: "我擁有授權或使用許可", licensedDetail: "需要提供合約、核准或授權參考。",
    evaluation: "僅用於評估", evaluationDetail: "正式環境使用和Pack晉級仍維持停用。",
    licenseReference: "授權或使用許可參考", licensePlaceholder: "合約ID、核准編號或URL",
    help: "此聲明會儲存為該版本的憑據。它不會驗證法律權利、簽發已簽署的權利記錄或發佈Design System Pack，仍需另行審查。",
    evaluationStatus: "僅限評估 · Pack晉級已停用", recordedStatus: "使用憑據已記錄 · 權利審查待處理 · Pack晉級已阻擋",
    recordFailed: "無法記錄來源使用憑據。", recording: "正在記錄", record: "記錄憑據",
  },
  readiness: {
    title: "生成準備狀態", show: "顯示準備條件詳情", hide: "隱藏準備條件詳情",
    readyCount: (ready, total) => `${total} 個條件中 ${ready} 個已就緒`, next: (label) => `下一步：${label}`,
    complete: "所有必要的準備條件均已完成", sourceMapping: "來源對應", designTokens: "設計權杖",
    visualEvidence: "視覺憑據", revisionDiff: "版本差異", assembly: "組裝驗證", candidate: "候選連接", runtimeProof: "執行階段證明",
    surfacesSelected: (accepted, total) => `${total} 個畫面中已選擇 ${accepted} 個`, decisionsPending: (pending) => `${pending} 項決策待處理`,
    previews: (rendered, requested, judgements) => `${requested} 個預覽中已產生 ${rendered} 個 · ${judgements} 項判定`,
    changed: (count, disposition) => `${count} 項變更 · ${disposition}`, notRequired: "無需執行",
  },
  advanced: {
    title: "進階來源審查", show: "開啟進階來源審查", hide: "關閉進階來源審查",
    summary: (pages, surfaces, tokens) => `${pages} 個頁面 · ${surfaces} 個畫面 · ${tokens} 項權杖決策待處理`,
  },
  revision: {
    title: "版本視覺差異", changedSurfaces: "已變更畫面", disposition: "處理結果", status: "狀態", reviewRequired: "需要視覺審查",
    reviewDetail: (count) => `編輯後的版本改變了 ${count} 個畫面的來源像素。生成前，必須由人工或Gemma核准這些預期差異。`,
  },
  sourceTree: "來源樹", applySuggestions: "套用建議", pageNodes: (nodes, sections) => `${nodes} 個節點 · ${sections} 個故事區段`,
  mapper: "頁面／故事對應", page: "頁面", browserProof: "Web Worker · 無擴充功能 · 已驗證受限的.fig匯出／重新開啟（僅彙總）",
  revisionAdmission: { admitted: "規範版本已准入", rejected: "僅瀏覽器候選 · 規範准入未完成", pending: "規範准入待處理" },
  component: {
    title: "元件變體編輯器", detail: "在瀏覽器Worker中執行受限編輯並重新開啟.fig；伺服器僅在驗證後准入結果版本。",
    revising: "正在建立版本", create: "建立元件版本", set: "元件集", variant: "變體",
    sourceOnly: "僅使用設計中讀取的值", extractor: "擷取器", sampled: "已取樣", valueCoverage: "值涵蓋率",
    complete: "完整", partial: "部分", legacyUnknown: "舊版／未知", editableSnapshot: "可編輯快照",
    available: "可用", unavailable: "不可用",
    semanticBoundary: "不會根據來源名稱推斷Cogo語意角色或行為。在精選契約准入前，INSTANCE_SWAP項目仍是未解決的插槽候選。",
    anatomy: "結構", descendantEvidence: "後代憑據", bounded: "受限", truncated: "已截斷", unresolvedSlots: "未解決插槽",
    setIdentity: "元件集識別", variantIdentity: "變體識別", overrideOpacity: "覆寫元件不透明度",
    opacity: "元件不透明度", overrideFill: "覆寫元件填滿", fillColor: "元件填滿顏色",
    fillBoundary: "僅可編輯第一個純色填滿。漸層和影像填滿會被拒絕。",
  },
  frame: {
    title: "來源畫框編輯器", retained: "建立新的.fig版本，不會覆寫保留的原始檔案。",
    boundary: "此處僅可修改名稱、寬度、高度、可見性和不透明度。顏色編輯使用元件編輯器，並且僅限第一個純色填滿。",
    revising: "正在建立版本", create: "建立版本", name: "畫框名稱", width: "寬度", height: "高度", visibility: "可見性",
    keep: "保留來源值", visible: "可見", hidden: "隱藏", overrideOpacity: "覆寫不透明度", opacity: "畫框不透明度",
    noCandidates: "此頁面沒有畫面候選。", candidate: "候選", role: "角色",
  },
  decisions: { accept: "接受", defer: "暫緩", reject: "拒絕", storyTitle: "故事標題" },
  statusLabel: (value) =>
    (({
      pending: "待處理", accepted: "已接受", rejected: "已拒絕", approved: "已核准",
      blocked: "已阻擋", admitted: "已准入", available: "可用", unavailable: "不可用",
      complete: "完成", completed: "已完成", missing: "未完成", bound: "已連接",
      passed: "通過", required: "必要", optional: "選用",
    } as Record<string, string>)[value] ?? value),
  surfaceClassification: (value) =>
    (({ mobile: "行動版", reference: "參考", "non-mobile": "參考畫面" } as Record<string, string>)[value] ?? value),
  surfaceRole: (value) =>
    (({ screen: "畫面", reference: "參考", annotation: "註解", cover: "封面", history: "歷程" } as Record<string, string>)[value] ?? value),
  tokens: {
    title: "設計權杖候選", acceptAll: "接受所有權杖候選", rejectAll: "拒絕所有權杖候選",
    summary: (pending, accepted, samples) => `${pending} 項待處理 · ${accepted} 項已接受 · ${samples} 個視覺樣本`,
    applying: "正在套用…", apply: "套用至此專案", reapply: "重新套用至此專案",
  },
  proof: { title: "證明範圍", accepted: "已確認", notClaimed: "未聲明", generation: "生成條件" },
};

export type OpenPencilSourcePanelCopy = typeof EN_SOURCE_PANEL_COPY;

export const OPENPENCIL_SOURCE_PANEL_COPY: Record<LocaleCode, OpenPencilSourcePanelCopy> = {
  en: EN_SOURCE_PANEL_COPY,
  ko: KO_SOURCE_PANEL_COPY,
  ja: JA_SOURCE_PANEL_COPY,
  zh: ZH_SOURCE_PANEL_COPY,
  "zh-TW": ZH_TW_SOURCE_PANEL_COPY,
};
